import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertCircle,
  Download, ArrowLeft, Loader2, Info, Table
} from 'lucide-react';
import axios from 'axios';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Expected columns configuration
const COLUMN_KEYS = {
  required: ['language_code', 'word', 'lemma'],
  optional: [
    'part_of_speech', 'ipa', 'definition', 'example', 'translation_en',
    'frequency_rank', 'domain', 'collocations_left', 'collocations_right',
    'synonyms', 'antonyms', 'etymology'
  ]
};

export default function BatchUpload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);
  
  const validateAndSetFile = useCallback((selectedFile) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const validExtensions = ['.xlsx', '.csv'];
    
    const hasValidExtension = validExtensions.some(ext => 
      selectedFile.name.toLowerCase().endsWith(ext)
    );
    
    if (!hasValidExtension && !validTypes.includes(selectedFile.type)) {
      toast.error(t('batchUpload.vocabulary.invalidType'));
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  }, [validateAndSetFile]);
  
  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  }, [validateAndSetFile]);
  
  const handleUpload = useCallback(async () => {
    if (!file) return;
    
    setUploading(true);
    setResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/batch-upload/vocabulary`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setResult(response.data);
      
      if (response.data.successful > 0) {
        toast.success(t('batchUpload.vocabulary.imported', { count: response.data.successful }));
      }
      if (response.data.failed > 0) {
        toast.warning(t('batchUpload.vocabulary.failedRows', { count: response.data.failed }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || t('batchUpload.vocabulary.uploadFailed'));
      setResult({
        total_rows: 0,
        successful: 0,
        failed: 1,
        skipped: 0,
        errors: [{ row: 0, error: err.response?.data?.detail || err.message }]
      });
    } finally {
      setUploading(false);
    }
  }, [file]);
  
  const downloadTemplate = useCallback(() => {
    // Create CSV template
    const headers = [...COLUMN_KEYS.required, ...COLUMN_KEYS.optional];
    
    const exampleRow = [
      'es',           // language_code
      'hablar',       // word
      'hablar',       // lemma
      'verb',         // part_of_speech
      '/aˈβlaɾ/',     // ipa
      'to speak | to talk',  // definition
      'Ella habla español.', // example
      'to speak',     // translation_en
      '150',          // frequency_rank
      'general',      // domain
      'querer | poder | saber',  // collocations_left
      'español | bien | claro',  // collocations_right
      'conversar | platicar',    // synonyms
      'callar | silenciar',      // antonyms
      'From Latin fabulare'      // etymology
    ];
    
    const csvContent = [
      headers.join(','),
      exampleRow.map(v => `"${v}"`).join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vocabulary_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);
  
  const clearFile = useCallback(() => {
    setFile(null);
    setResult(null);
  }, []);
  
  // Check admin access - moved after all hooks
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              Only administrators can access the batch upload feature.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/users')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Batch Vocabulary Upload</h1>
              <p className="text-muted-foreground">Import words from Excel or CSV files</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Instructions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table className="h-5 w-5" />
              {t('batchUpload.vocabulary.fileFormat')}
            </CardTitle>
            <CardDescription>
              {t('batchUpload.vocabulary.fileFormatDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Required columns */}
            <div>
              <h4 className="font-semibold text-sm text-destructive mb-2">{t('batchUpload.vocabulary.requiredColumns')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {COLUMN_KEYS.required.map(name => (
                  <div key={name} className="p-2 rounded bg-destructive/10 border border-destructive/20">
                    <code className="text-sm font-mono">{name}</code>
                    <p className="text-xs text-muted-foreground mt-1">{t(`batchUpload.vocabulary.columnDescriptions.${name}`)}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Optional columns */}
            <div>
              <h4 className="font-semibold text-sm text-muted-foreground mb-2">{t('batchUpload.vocabulary.optionalColumns')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {COLUMN_KEYS.optional.map(name => (
                  <div key={name} className="p-2 rounded bg-muted/50">
                    <code className="text-sm font-mono">{name}</code>
                    <p className="text-xs text-muted-foreground mt-1">{t(`batchUpload.vocabulary.columnDescriptions.${name}`)}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Tips */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-600">{t('batchUpload.vocabulary.tips')}</p>
                <ul className="mt-1 space-y-1 text-muted-foreground">
                  <li>• {t('batchUpload.vocabulary.tip1', { symbol: '|' })}</li>
                  <li>• {t('batchUpload.vocabulary.tip2')}</li>
                  <li>• {t('batchUpload.vocabulary.tip3')}</li>
                </ul>
              </div>
            </div>
            
            <Button variant="outline" onClick={downloadTemplate} className="gap-2">
              <Download className="h-4 w-4" />
              {t('batchUpload.vocabulary.downloadTemplate')}
            </Button>
          </CardContent>
        </Card>
        
        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload File
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver 
                  ? 'border-primary bg-primary/5' 
                  : file 
                    ? 'border-green-500 bg-green-500/5' 
                    : 'border-muted-foreground/25 hover:border-muted-foreground/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-green-500" />
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFile}
                  >
                    {t('batchUpload.vocabulary.remove')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {t('batchUpload.vocabulary.dragAndDrop')}
                  </p>
                  <label>
                    <input
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button variant="secondary" className="cursor-pointer" asChild>
                      <span>{t('batchUpload.vocabulary.browseFiles')}</span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground">
                    {t('batchUpload.vocabulary.supported')}
                  </p>
                </div>
              )}
            </div>
            
            {/* Upload button */}
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              className="w-full gap-2"
              size="lg"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('batchUpload.vocabulary.uploading')}
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  {t('batchUpload.vocabulary.uploadAndImport')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
        
        {/* Results Card */}
        {result && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.failed === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : result.successful === 0 ? (
                  <XCircle className="h-5 w-5 text-destructive" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                )}
                Upload Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg bg-muted">
                  <p className="text-2xl font-bold">{result.total_rows}</p>
                  <p className="text-xs text-muted-foreground">{t('batchUpload.vocabulary.totalRows')}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-green-500/10">
                  <p className="text-2xl font-bold text-green-600">{result.successful}</p>
                  <p className="text-xs text-muted-foreground">{t('batchUpload.vocabulary.successful')}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-destructive/10">
                  <p className="text-2xl font-bold text-destructive">{result.failed}</p>
                  <p className="text-xs text-muted-foreground">{t('batchUpload.vocabulary.failed')}</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted">
                  <p className="text-2xl font-bold">{result.skipped}</p>
                  <p className="text-xs text-muted-foreground">{t('batchUpload.vocabulary.skipped')}</p>
                </div>
              </div>
              
              {/* Progress bar */}
              {result.total_rows > 0 && (
                <div className="space-y-1">
                  <Progress 
                    value={(result.successful / result.total_rows) * 100} 
                    className="h-2"
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {t('batchUpload.vocabulary.successRate', { percent: Math.round((result.successful / result.total_rows) * 100) })}
                  </p>
                </div>
              )}
              
              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-destructive">{t('batchUpload.vocabulary.errors')}</h4>
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {result.errors.map((err, idx) => (
                      <div 
                        key={idx} 
                        className="text-sm p-2 rounded bg-destructive/10 border border-destructive/20"
                      >
                        <span className="font-mono text-destructive">{t('batchUpload.vocabulary.row', { row: err.row })}</span>{' '}
                        <span className="text-muted-foreground">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
